import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  QueryList,
  ViewChildren,
  inject
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { DropdownComponent } from 'src/app/components/shared/dropdown';
import { TrashComponent } from 'src/app/components/icons/trash';
import { PlusComponent } from 'src/app/components/icons/plus';
import { LocaleService } from 'src/app/services/locale';
import { CategoryService } from 'src/app/services/category';

@Component({
  selector: 'app-category5-question4',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    TrashComponent,
    PlusComponent
  ],
  template: `
    <div class="text-[12px] text-[#616161] mt-1">
      {{ strings[language].answer }}
    </div>

    <!-- Usage toggle -->
    <div class="flex flex-wrap gap-4 sm:gap-6 mt-4">
      <div
        (click)="toggleUsage(true)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_yes }}</span>
      </div>
      <div
        (click)="toggleUsage(false)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="!usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_no }}</span>
      </div>
    </div>

    <!-- Add Portfolio -->
    <div class="flex justify-end mb-2 items-center" *ngIf="usage">
      <div
        (click)="addProductGroup()"
        class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-md cursor-pointer"
      >
        <app-plus></app-plus>
      </div>
    </div>

    <form *ngIf="usage" [formGroup]="form" class="space-y-10 mt-4">
      <div formArrayName="productGroups" class="space-y-6">
        <div
          *ngFor="let group of productGroups.controls; let i = index"
          [formGroupName]="i"
          class="border p-4 rounded-lg space-y-4"
        >
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold">
              {{ strings[language].main_label }} {{ i + 1 }}
            </h2>
            <div
              class="cursor-pointer"
              *ngIf="productGroups.length > 1"
              (click)="removeProductGroup(i)"
            >
              <app-trash></app-trash>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_1
              }}</label>
              <app-input
                [placeholder]="strings[language].placeholder_portfolio_label"
                [control]="getFormControl(group, 'product_label')"
              ></app-input>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_4
              }}</label>
              <app-dropdown
                [options]="yearOptions"
                [placeholder]="strings[language].placeholder_reporting_year"
                [control]="getFormControl(group, 'rep_year')"
              ></app-dropdown>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_currency
              }}</label>
              <app-input
                [placeholder]="strings[language].placeholder_currency"
                [control]="getFormControl(group, 'currency')"
              ></app-input>
            </div>

            <div class="col-span-2">
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_6
              }}</label>
              <textarea
                [placeholder]="strings[language].placeholder_notes"
                class="w-full border border-[#CED4DA] rounded p-2"
                rows="3"
                [formControl]="getFormControl(group, 'note')"
              ></textarea>
            </div>
          </div>

          <!-- Investments -->
          <div formArrayName="investments" class="border rounded p-4">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium">
                {{ strings[language].main_label_1 }}
              </h3>
              <button
                type="button"
                class="bg-[#F8F9FA] px-3 py-2 rounded text-white"
                (click)="addInvestmentRow(getFormArray(group, 'investments'))"
              >
                <app-plus></app-plus>
              </button>
            </div>

            <div
              *ngFor="
                let row of getFormArray(group, 'investments').controls;
                let j = index
              "
              [formGroupName]="j"
              class="border rounded-lg p-4 mb-4"
            >
              <div class="flex justify-end mt-4">
                <button
                  type="button"
                  class="text-red-600 hover:text-red-800"
                  (click)="
                    removeInvestmentRow(getFormArray(group, 'investments'), j)
                  "
                >
                  <app-trash></app-trash>
                </button>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    strings[language].label_asset_class
                  }}</label>
                  <app-dropdown
                    [options]="assetClassOptions"
                    [placeholder]="strings[language].placeholder_asset_class"
                    [control]="getFormControl(row, 'asset_class')"
                  ></app-dropdown>
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    strings[language].label_investment_value
                  }}</label>
                  <app-input
                    type="number"
                    [placeholder]="
                      strings[language].placeholder_investment_value
                    "
                    [control]="getFormControl(row, 'inv_value')"
                  ></app-input>
                </div>
                <div
                  *ngIf="
                    getFormControl(row, 'asset_class')?.value ===
                    assetClassOptions[2].value
                  "
                >
                  <label class="block text-sm font-medium mb-1">{{
                    strings[language].label_share_pct
                  }}</label>
                  <app-input
                    type="number"
                    [placeholder]="strings[language].placeholder_share_pct"
                    [control]="getFormControl(row, 'share_pct')"
                  ></app-input>
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    strings[language].label_ef_override
                  }}</label>
                  <app-input
                    type="number"
                    [placeholder]="strings[language].placeholder_ef_override"
                    [control]="getFormControl(row, 'ef_override')"
                  ></app-input>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  `
})
export class Category5Question4Component implements OnInit {
  @ViewChildren('closeDropdown') dropdownContainers!: QueryList<ElementRef>;
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  form!: FormGroup;
  usage = false;

  strings: any = {
    english: {
      answer: 'Please fill in the required data if yes to proceed.',
      choice_yes: 'Yes',
      choice_no: 'No',
      main_label: 'New Portfolio',
      label_1: 'Portfolio label',
      label_currency: 'Currency',
      label_4: 'Reporting year',
      label_6: 'Notes',
      main_label_1: 'Investments',
      label_asset_class: 'Asset class',
      label_investment_value: 'Investment value (EUR)',
      label_share_pct: 'Share of project costs (%)',
      label_ef_override: 'Emission-factor override',

      // Placeholders
      placeholder_portfolio_label: 'Enter portfolio label',
      placeholder_reporting_year: 'Select reporting year',
      placeholder_currency: 'Enter currency (e.g. EUR)',
      placeholder_notes: 'Enter any additional notes',
      placeholder_asset_class: 'Select asset class',
      placeholder_investment_value: 'Enter investment value',
      placeholder_share_pct: 'Enter share percentage',
      placeholder_ef_override: 'Enter emission factor override'
    },
    italian: {
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      choice_yes: 'Sì',
      choice_no: 'No',
      main_label: 'Portafoglio',
      label_1: 'Nome del portafoglio',
      label_4: 'Anno di rendicontazione',
      label_6: 'Note',
      main_label_1: 'Investimenti',
      label_asset_class: 'Classe di attività',
      label_investment_value: "Valore dell'investimento (EUR)",
      label_share_pct: 'Quota dei costi di progetto (%)',
      label_ef_override: 'Fattore di emissione personalizzato',

      // Placeholders
      placeholder_portfolio_label: 'Inserisci etichetta del portafoglio',
      placeholder_reporting_year: 'Seleziona anno di rendicontazione',
      placeholder_currency: 'Inserisci valuta (es. EUR)',
      placeholder_notes: 'Inserisci eventuali note aggiuntive',
      placeholder_asset_class: 'Seleziona classe di attività',
      placeholder_investment_value: "Inserisci valore dell'investimento",
      placeholder_share_pct: 'Inserisci percentuale di quota',
      placeholder_ef_override: 'Inserisci fattore di emissione personalizzato'
    }
  };

  assetClassOptions = [
    {
      value:
        'financial_services-type_financial_intermediation_services_except_insurance_pension_funding_services',
      label:
        this.language === 'english'
          ? 'Listed equity & corporate bonds'
          : 'Azioni quotate e obbligazioni societarie'
    },
    {
      value:
        'financial_services-type_services_auxiliary_to_financial_intermediation',
      label:
        this.language === 'english' ? 'Corporate loans' : 'Prestiti aziendali'
    },
    {
      value:
        'financial_services-type_financial_intermediation_except_insurance_and_pension_funding-basis_industry',
      label:
        this.language === 'english' ? 'Project finance' : 'Finanza di progetto'
    }
  ];

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      productGroups: this.fb.array([])
    });

    const categoriesRaw = localStorage.getItem('categories');
    let categories = [];

    if (categoriesRaw) {
      try {
        categories = JSON.parse(categoriesRaw);
        while (categories.length < 5)
          categories.push(this.createDefaultCategory());

        const answers = categories[4]?.questions?.[3]?.answers;
        if (answers && Array.isArray(answers)) {
          this.usage = true;
          answers.forEach((ans: any) => {
            const group = this.fb.group({
              product_label: [ans.product_label || '', Validators.required],
              rep_year: [ans.rep_year || '', Validators.required],
              note: [ans.note || ''],
              currency: [{ value: 'EUR', disabled: true }],
              investments: this.fb.array([])
            });

            this.productGroups.push(group);

            ans.investments?.forEach((inv: any) => {
              const row = this.addInvestmentRow(
                this.getFormArray(group, 'investments')
              );
              row.patchValue(inv);
            });
          });
        }
      } catch (e) {
        console.error('Failed to parse localStorage categories:', e);
      }
    }

    this.productGroups.valueChanges.subscribe(() => this.saveToLocalStorage());

    if (this.usage && this.productGroups.length === 0) this.addProductGroup();
  }

  get productGroups(): FormArray {
    return this.form.get('productGroups') as FormArray;
  }

  getFormControl(group: AbstractControl, field: string): FormControl {
    return group.get(field) as FormControl;
  }

  getFormArray(group: AbstractControl, field: string): FormArray {
    return group.get(field) as FormArray;
  }

  toggleUsage(value: boolean): void {
    this.usage = value;
    if (value && this.productGroups.length === 0) this.addProductGroup();
    if (!value) {
      this.productGroups.clear();
      this.saveToLocalStorage();
    }
  }

  addProductGroup(): void {
    const group = this.fb.group({
      product_label: ['', Validators.required],
      rep_year: ['', Validators.required],
      note: [''],
      currency: [{ value: 'EUR', disabled: true }],
      investments: this.fb.array([])
    });
    this.productGroups.push(group);
    this.addInvestmentRow(this.getFormArray(group, 'investments'));
  }

  removeProductGroup(index: number): void {
    this.productGroups.removeAt(index);
  }

  addInvestmentRow(array: FormArray): FormGroup {
    const group = this.fb.group({
      asset_class: ['', Validators.required],
      inv_value: [null, [Validators.required, Validators.min(0.0000001)]],
      share_pct: [null, [Validators.min(0), Validators.max(100)]],
      ef_override: [null, Validators.min(0.0000001)]
    });

    group.get('asset_class')?.valueChanges.subscribe(value => {
      const shareControl = group.get('share_pct');
      if (value === 'project_finance') {
        shareControl?.addValidators(Validators.required);
      } else {
        shareControl?.clearValidators();
      }
      shareControl?.updateValueAndValidity();
    });

    array.push(group);
    return group;
  }

  removeInvestmentRow(array: FormArray, index: number): void {
    array.removeAt(index);
  }

  getShareSum(array: FormArray): number {
    return array.controls.reduce((sum, ctrl) => {
      const val = ctrl.get('share_pct')?.value;
      return sum + (parseFloat(val) || 0);
    }, 0);
  }

  saveToLocalStorage(): void {
    const answers = this.productGroups.controls.map(group => {
      const invArray = this.getFormArray(group, 'investments');
      return {
        product_label: group.get('product_label')?.value,
        rep_year: group.get('rep_year')?.value,
        note: group.get('note')?.value,
        currency: 'EUR',
        investments: invArray.controls.map(ctrl => {
          const assetClassValue = ctrl.get('asset_class')?.value;
          let assetClassLabel = '';
          const foundOption = this.assetClassOptions.find(
            option => option.value === assetClassValue
          );
          if (foundOption) {
            assetClassLabel = foundOption.label;
          }

          return {
            asset_class: assetClassValue,
            asset_class_label: assetClassLabel,
            inv_value: ctrl.get('inv_value')?.value,
            share_pct: ctrl.get('share_pct')?.value,
            ef_override: ctrl.get('ef_override')?.value
          };
        })
      };
    });

    let categories = (this.categoryService.categories() as any[]) || [];
    while (categories.length < 5) categories.push(this.createDefaultCategory());
    if (!categories[4].questions)
      categories[4].questions = [this.createDefaultQuestion()];
    if (categories[4].questions.length === 0)
      categories[4].questions.push(this.createDefaultQuestion());

    categories[4].questions[3].answers = this.usage ? answers : null;
    this.categoryService.persistCategories(categories);
  }

  private createDefaultCategory() {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      questions: [this.createDefaultQuestion()]
    };
  }

  private createDefaultQuestion() {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      answers: null
    };
  }
  currentYear = new Date().getFullYear();
  startYear = 2019;
  yearOptions = Array.from(
    { length: this.currentYear - this.startYear + 1 },
    (_, i) => {
      const year = this.startYear + i;
      return { label: String(year), value: year };
    }
  );
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {}
}
